import { useCallback, useMemo, useRef, useState } from "react";
import { cx } from "../../utils/classNames";
import { ToastContext } from "./toastContext";
import ui from "./ui.module.css";

const DURATION_MS = 4500;

const TONES = {
  success: { className: ui.alertSuccess, icon: "bi-check-circle" },
  error: { className: ui.alertDanger, icon: "bi-exclamation-circle" },
  info: { className: ui.alertInfo, icon: "bi-info-circle" },
};

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (type, message) => {
      const id = ++nextId.current;
      setToasts((prev) => [...prev, { id, type, message }]);
      setTimeout(() => dismiss(id), DURATION_MS);
    },
    [dismiss],
  );

  const api = useMemo(
    () => ({
      success: (message) => show("success", message),
      error: (message) => show("error", message),
      info: (message) => show("info", message),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}

      <div className={ui.toastStack} aria-live="polite">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cx(ui.toast, TONES[toast.type].className)}
            role="status"
          >
            <i className={`bi ${TONES[toast.type].icon}`}></i>
            <span>{toast.message}</span>
            <button
              type="button"
              className={ui.iconButton}
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss"
            >
              <i className="bi bi-x"></i>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
